import { AccountFeatures, AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useMemo, useState } from "react"
import { Button, Col, Row } from "react-bootstrap"
import { FormattedHTMLMessage, FormattedMessage, defineMessages, injectIntl } from "react-intl"
import Icon from "../common/icon/icon.component"
import IntegrationButtonWrapper from "../common/rich-text/feature-protection-wrappers/integrationButtonWrapper.component"
import { INTEGRATION_KEY } from "../state/integration/Constants"
import { getNylasVersion } from "../utils/common/utils"


////////////////////
// Types & Constants
////////////////////
type IntegrationConfigurationAttributes = {
    accountId: string;
    configuration: {
        accountId: string;
        email?: string;
        provider?: string;
    };
    deleted: boolean;
    deletedAt: string | null;
    deletedBy: string | null;
    enabled: boolean;
    errorMessage: string;
    externalAccountId: string;
    enabledFeatures: {
        "integrated-payment-bank-transfer"?: boolean;
        scheduling?: boolean;
        "send-email"?: boolean;
    }
    features: string[];
    integrationKey: string;
    locked: boolean;
    userId: string;
}

type IntegrationConfiguration = {
    id: string;
    type: string;
    attributes: IntegrationConfigurationAttributes;
}

type AccountUserAttributes = {
    accountId: string
    firstName: string
    name: string
    lastName: string
    locationId: string
    role: string
    email?: string
    disabled?: boolean
    integrationConfiguration: IntegrationConfigurationAttributes | null
    integrationId?: string
}

type AccountUser = {
    id: string,
    type: string,
    attributes: AccountUserAttributes
}

type SchedulePagesList = {
    title: string,
    ID: string,
    fromEmail: string
}

export const NYLAS_MIGRATION_PAGE_MESSAGES = defineMessages({
    NYLAS_MIGRATION_PAGE_BREADCRUMB: {
        id: "breadcrumbs.nylas_migration_page",
        defaultMessage: "Action Required",
    },
})

const messages = defineMessages({
    email_users_text: {
        id: "nylas_migration_component.email_users_text",
        defaultMessage: "We are upgrading emails to a new platform. As of <b>February 20th</b>, " +
        "emails sent from your QuoteMachine account will be disrupted if your account is not migrated. " +
        "To avoid interruptions, migrate now and prepare ahead of time.",
    },
    email_users_bullet_1: {
        id: "nylas_migration_component.email_users_bullet_1",
        defaultMessage: "Clicking <b>‘Start account migration’</b> will immediately disable email connections for your account.",
    },
    email_users_bullet_2: {
        id: "nylas_migration_component.email_users_bullet_2",
        defaultMessage: "You will need to reconnect your email to resume sending emails through QuoteMachine.",
    },
    email_users_bullet_3: {
        id: "nylas_migration_component.email_users_bullet_3",
        defaultMessage: "Advise your team to reconnect their email accounts before <b>February 20th</b> to avoid disruptions.",
    },
    email_users_when_ready: {
        id: "nylas_migration_component.email_users_when_ready",
        defaultMessage: "When ready, click the button below to begin. If migration is not completed by <b>February 20th</b>, " +
        "the system will automatically update, and any emails not reconnected will be disabled until restored.",
    },
    scheduling_users_text: {
        id: "nylas_migration_component.scheduling_users_text",
        defaultMessage: "We are upgrading emails to a new platform. As of <b>February 20th</b>, " +
        "email and appointment functionality on your QuoteMachine account will be disrupted if " +
        "your account isn't migrated and reconnected. To avoid interruptions, migrate now and prepare ahead of time.",
    },
    scheduling_users_bullet_1: {
        id: "nylas_migration_component.scheduling_users_bullet_1",
        defaultMessage: "Clicking <b>‘Start account migration’</b> will immediately disable email connections " + 
        "for your account. This will also disable calendars and scheduling pages."
    },
    scheduling_users_bullet_2: {
        id: "nylas_migration_component.scheduling_users_bullet_2",
        defaultMessage: "You will need to reconnect your email to resume sending emails through QuoteMachine."
    },
    scheduling_users_bullet_3: {
        id: "nylas_migration_component.scheduling_users_bullet_3",
        defaultMessage: "Notify your team to reconnect their email accounts before <b>February 20th</b> to avoid disruptions."
    },
    scheduling_users_when_ready: {
        id: "nylas_migration_component.scheduling_users_when_ready",
        defaultMessage: "When ready, click the button below to begin. If migration is not completed by <b>February 20th</b>, " + 
        "the system will automatically update, and your team will need to restore functionality to continue using email and appointments.",
    },
    scheduling_users_reminder_migration_incomplete: {
        id: "nylas_migration_component.scheduling_users_reminder_migration_incomplete",
        defaultMessage: "<b class='text-danger'>Reminder: Do not recreate calendars or scheduling pages " + 
        "until the emails are reconnected.</b><br /><br /> Follow the steps in these articles to complete set up:"
    },
    scheduling_users_reminder_migration_complete: {
        id: "nylas_migration_component.scheduling_users_reminder_migration_complete",
        defaultMessage: "<b class='text-danger'>Remember to recreate calendars and scheduling pages as needed. " + 
            "Follow the steps in these articles:</b>"
    },
    scheduling_users_articles_2: {
        id: "nylas_migration_component.scheduling_users_articles_2",
        defaultMessage: "Setting up a calendar in QuoteMachine"
    },
    scheduling_users_articles_3: {
        id: "nylas_migration_component.scheduling_users_articles_3",
        defaultMessage: "Creating Scheduling Pages in QuoteMachine"
    },
    connect_btn_label: {
        id: "nylas_migration_component.connect_email_account_btn_label",
        defaultMessage: "Connect your email account",
    },
    email_account_migration_success: {
        id: "nylas_migration_component.email_account_migration_success",
        defaultMessage: "Your account was successfully migrated! Follow the steps below to reconnect your email—it takes less than 2 minutes.",
    },
    scheduling_account_migration_success: {
        id: "nylas_migration_component.scheduling_account_migration_success",
        defaultMessage: "Your account was successfully migrated! Next steps: <br /> " +
        "1. Reconnect your email accounts to resume sending emails. <br /> 2. Set up " +
        "your calendars in Sales tools. <br />3. Recreate your scheduling pages to ensure customers can continue booking appointments.",
    },
    migration_completed_success: {
        id: "nylas_migration_component.migration_completed_success",
        defaultMessage: "Migration completed successfully",
    },
    users_missing_migration_label: {
        id: "nylas_migration_component.users_missing_migration_label",
        defaultMessage: "Other users need to reconnect their email accounts.",
    },
    email_migration_status_need_migration: {
        id: "nylas_migration_component.email_migration_status_need_migration",
        defaultMessage: "Needs reconnection"
    },
    email_migration_status_migrated: {
        id: "nylas_migration_component.email_migration_status_migrated",
        defaultMessage: "Connected"
    },
    start_account_migration_btn: {
        id: "nylas_migration_component.start_account_migration_btn",
        defaultMessage: "Start account migration",
    },
    user_name_header: {
        id: "nylas_migration_component.user_name_header",
        defaultMessage: "User" 
    },
    user_email_header: {
        id: "nylas_migration_component.user_email_header",
        defaultMessage: "Email" 
    },
    status_header: {
        id: "nylas_migration_component.status_header",
        defaultMessage: "Status"
    },
    scheduling_page_list_table_header: {
        id: "nylas_migration_component.scheduling_page_list_table_header",
        defaultMessage: "Scheduling pages to recreate"
    },
    scheduling_page_identifier_header: {
        id: "nylas_migration_component.scheduling_page_identifier_header",
        defaultMessage: "ID" 
    },
    scheduling_page_title_header: {
        id: "nylas_migration_component.scheduling_page_title_header",
        defaultMessage: "Title" 
    },
    scheduling_page_from_header: {
        id: "nylas_migration_component.scheduling_page_from_header",
        defaultMessage: "From email" 
    },
})

////////////////////
// Helper functions
////////////////////
function renderNameColumn(matchedUsers: AccountUser[]) {
    return (
        <Col sm={3} className="mt-4 mb-1">
            <div style={{ color: "#234dd0", fontWeight: 500 }}>
                <FormattedMessage {...messages.user_name_header} />
            </div>
            {matchedUsers.map((user) => (
                <div key={user.id}>
                    {user.attributes.firstName} {user.attributes.lastName}
                </div>
            ))}
        </Col>
    )
}

function renderEmailColumn(matchedUsers: AccountUser[]) {
    return (
        <Col sm={5} className="mt-4 mb-1">
            <div style={{ color: "#234dd0", fontWeight: 500 }}>
                <FormattedMessage {...messages.user_email_header} />
            </div>
            {matchedUsers.map((user) => (
                <div key={user.id}>
                    {user.attributes.integrationConfiguration?.configuration.email}
                </div>
            ))}
        </Col>
    )
}

function emailStatus (integrationKey: string | undefined) {
    if (integrationKey === INTEGRATION_KEY.NYLAS) {
        return (<>
            <FormattedMessage {...messages.email_migration_status_need_migration} />
            <Icon value={"exclamation"} style={{ color: "orange"}}/>
        </>)
    } else if (integrationKey === INTEGRATION_KEY.NYLASV3) {
        return (<>
            <FormattedMessage {...messages.email_migration_status_migrated} />
            <Icon value={"check"} style={{ color: "green", marginLeft: "0.3rem"}}/>
        </>)
    } else {
        return "Unknown"
    }
}

function renderStatusColumn(matchedUsers) {
    return (
        <Col sm={3} className="mt-4 mb-1">
            <div style={{ color: "#234dd0", fontWeight: 500 }}>
                <FormattedMessage {...messages.status_header} />
            </div>
            {matchedUsers.map((user) => (
                <div key={user.id}>
                    {emailStatus(user.attributes.integrationConfiguration?.integrationKey) || "Unknown"}
                </div>
            ))}
        </Col>
    )
}

function renderSchedulePageList(schedulePagesList: SchedulePagesList[]) {
    return (<>
        <Col sm={3} className="mb-1">
            <div style={{ color: "#234dd0", fontWeight: 500 }}>
                <FormattedMessage {...messages.scheduling_page_identifier_header} />
            </div>
            {schedulePagesList.map((schpg) => (
                <div key={schpg.ID}>
                    {schpg.ID}
                </div>
            ))}
        </Col>
        <Col sm={5} className="mb-1">
            <div style={{ color: "#234dd0", fontWeight: 500 }}>
                <FormattedMessage {...messages.scheduling_page_title_header} />
            </div>
            {schedulePagesList.map((schpg) => (
                <div key={schpg.ID}>
                    {schpg.title}
                </div>
            ))}
        </Col>
        <Col sm={4} className="mb-1">
            <div style={{ color: "#234dd0", fontWeight: 500 }}>
                <FormattedMessage {...messages.scheduling_page_from_header} />
            </div>
            {schedulePagesList.map((schpg) => (
                <div key={schpg.ID}>
                    {schpg.fromEmail}
                </div>
            ))}
        </Col>
    </>)
}

////////////////////
// Component
////////////////////
const NylasMigrationComponent = () => {
    const accountId = AppContext.getAccount().id
    const userLoggedInId = AppContext.getUserLoggedIn().id
    const isCurrentUserAdmin = AppContext.getUserLoggedIn().attributes.role.attributes.key === "admin"

    // Nylas redirect URL to v3
    const nylasUrl = AppContext.getOptions().apiUrl + "/openid/connect/"+ INTEGRATION_KEY.NYLASV3 + "/" +
        userLoggedInId + "?r=/users/edit/" + userLoggedInId

    // Validate if the account has scheduling enabled
    const isSchedulingEnabled = AppContext.accountFeatureIsEnabled("scheduling")
    
    const [matchedUsers, setMatchedUsers] = useState<AccountUser[]>([])
    const [currentIntegrationId, setCurrentIntegrationId] = useState("")
    const [isUserLoggedInMigrated, setIsUserLoggedInMigrated] = useState(false)
    const [migrationFlagChanged, setMigrationFlagChanged] = useState(false)
    const [migrationCompleted, setMigrationCompleted] = useState(false)
    const [nylasKey, setNylasKey] = useState(getNylasVersion())
    const [userLoggedInEmail, setUserLoggedInEmail] = useState("")
    const [schedulePagesListing, setSchedulePagesListing] = useState<SchedulePagesList[]>([])
    
    const updateNylasFlag = (accountId) => {
        const promise = AppContext.getApiClient().patch(`/accounts/${accountId}/trigger-nylas-migration`, {
            flag: JSON.stringify({ enableNylasV3: true })
        })

        if (promise) {
            promise.then(async () => {
                setMigrationFlagChanged(true)
                setNylasKey(INTEGRATION_KEY.NYLASV3)
                
                // Adding a 2 seconds delay within fetchAccountDetails before API call
                await new Promise(resolve => setTimeout(resolve, 2000))
                
                await fetchAccountDetails()
            }).catch((error) => {
                console.error("Error updating Nylas flag:", error)
            })
        }
    }

    const fetchAccountDetails = async () => {
        try {
            const accountDetails = await AppContext.getApiClient().get(`/accounts/${accountId}`)
            
            if (
                accountDetails 
                && accountDetails.attributes?.modelAccountID.length > 0 
                && accountDetails.attributes.modelAccountID.trim().startsWith("{")
            ) {

                const sanitizedJsonString = `[${accountDetails.attributes.modelAccountID}]`

                const parsedData: SchedulePagesList[] = JSON.parse(sanitizedJsonString)
                
                const schedulePagesListing: SchedulePagesList[] = parsedData.map((item) => ({
                    title: item.title,
                    ID: item.ID,
                    fromEmail: item.fromEmail,
                }))
                
                setSchedulePagesListing(schedulePagesListing)
            }
        } catch (error) {
            console.error("Error fetching account details:", error)
        }
    }

    const fetchData = async () => {
        try {
            let usersObj: AccountUser[] = []
            let accIntegrationsObj: IntegrationConfiguration[] = []
            const [userList, accIntegrations] = await Promise.all([
                AppContext.getApiClient().get("/users"),
                AppContext.getApiClient().get("/integration-configurations")
            ])

            if (userList?.data) {
                usersObj = userList.data as AccountUser[]
            }

            if (accIntegrations?.data) {
                accIntegrationsObj = accIntegrations.data as IntegrationConfiguration[]
            }

            if(usersObj.length > 0 && accIntegrationsObj.length > 0) {
                const matched = matchUsersWithIntegrations(usersObj, accIntegrationsObj)
                setMatchedUsers(matched)
            }

        } catch (error) {
            console.error("Error fetching data:", error)
        }
    }

    const matchUsersWithIntegrations = (usersObj: AccountUser[], accIntegrationsObj: IntegrationConfiguration[]) => {
        const matchedUsers = usersObj.map(user => {
            const userId = user.id
            const integration = accIntegrationsObj.find(integration => 
                [INTEGRATION_KEY.NYLAS, INTEGRATION_KEY.NYLASV3].includes(integration.attributes.integrationKey) &&
                integration.attributes.userId === userId &&
                integration.attributes.deleted === false
            )
            return integration
                ? {
                    ...user,
                    attributes: {
                        ...user.attributes,
                        integrationConfiguration: integration.attributes,
                        integrationId: integration.id
                    },
                }
                : null
        }).filter(Boolean) as AccountUser[]

        // Validate if user logged in has already migrated
        setIsUserLoggedInMigrated(hasUserLoggedInMigrated(matchedUsers))

        // Get the current integration id for the user logged in
        setCurrentIntegrationId(getCurrentUserIntegrationId(matchedUsers))

        // Get current user email
        setUserLoggedInEmail(getCurrentUserEmail(usersObj))

        //Validate if the account has completed migration
        const migrationIncomplete = accIntegrationsObj.some(integration => 
            [INTEGRATION_KEY.NYLAS].includes(integration.attributes.integrationKey) &&
            integration.attributes.deleted === false
        )
        setMigrationCompleted(!migrationIncomplete)

        return matchedUsers
    }

    const hasUserLoggedInMigrated = (matchedUsers: AccountUser[]) => {
        return matchedUsers.some(user => 
            user.id === userLoggedInId && 
            user.attributes.integrationConfiguration?.integrationKey === INTEGRATION_KEY.NYLASV3
        )
    }

    const getCurrentUserIntegrationId = (matchedUsers: AccountUser[]) => {
        return matchedUsers.find(user => 
            user.id === userLoggedInId && 
            user.attributes.integrationConfiguration?.integrationKey === INTEGRATION_KEY.NYLAS
        )?.attributes.integrationId || ""
    }

    const getCurrentUserEmail = (matchedUsers: AccountUser[]) => {
        return matchedUsers.find(user => 
            user.id === userLoggedInId
        )?.attributes.email || ""
    }

    const disconnectUserIntegration  = async (event: React.MouseEvent) => {
        event.preventDefault()

        if (currentIntegrationId !== "") {
            await AppContext.getApiClient().delete("/integration-configurations/" + currentIntegrationId)
        }

        // Then continue with the integration logic or redirect:
        window.location.href = nylasUrl
    }

    const callToActionBtn = useMemo(() => {
        if (nylasKey === INTEGRATION_KEY.NYLAS && !isUserLoggedInMigrated) {
            return (<>
                {!isCurrentUserAdmin && <div className="alert alert-danger">
                    <FormattedMessage 
                        id="nylas_migration_component.must_be_admin_label"
                        defaultMessage={"An Administrator must start the account migration"} />
                </div>}
                <Button
                    type="button"
                    variant="contained"
                    disabled={!isCurrentUserAdmin}
                    className="btn btn-primary"
                    onClick={() => updateNylasFlag(accountId)}
                >
                    <FormattedMessage {...messages.start_account_migration_btn}
                    />
                </Button>
            </>)
        } else if (nylasKey === INTEGRATION_KEY.NYLASV3 && !isUserLoggedInMigrated) {
            return (
                <div onClick={disconnectUserIntegration}>
                    <IntegrationButtonWrapper
                        module={AccountFeatures.FeatureCustomEmail}
                        redirectToAccount={true}
                        to={nylasUrl}
                        integrationKey={getNylasVersion()}
                        className={"btn btn-outline-primary"}
                        protectedClassName={"btn btn-outline-primary"}
                        premium={false}
                        protectionRule={AppContext.accountFeatureIsEnabled(AccountFeatures.FeatureCustomEmail)}
                    >
                        <span>
                            <Icon value={"envelope"} className={"mr-2 fa-lg"} />
                            {userLoggedInEmail.length > 0 ? <FormattedMessage 
                                id="nylas_migration_component.start_email_migration_btn_label"
                                defaultMessage={"Reconnect your email account: {userLoggedInEmail}"} 
                                values={{ userLoggedInEmail }} />
                                : <FormattedMessage 
                                    id="nylas_migration_component.start_email_migration_user_btn_label"
                                    defaultMessage={"Reconnect your email account"} />
                            }
                            
                        </span>
                    </IntegrationButtonWrapper>
                </div>
            )
        } else if (nylasKey === INTEGRATION_KEY.NYLASV3 && isUserLoggedInMigrated) {
            return (
                <div className="alert alert-warning" >
                    <FormattedMessage {...messages.users_missing_migration_label} />
                </div>
            )
        }
        return null
    }, [nylasKey, isUserLoggedInMigrated, accountId, currentIntegrationId, userLoggedInEmail])

    useEffect(() => {
        fetchData()
        if(nylasKey === INTEGRATION_KEY.NYLASV3) {
            fetchAccountDetails()
        }
    }, [])


    return (
        <>
            <div className="ui-module m-2 pb-2" style={{ textAlign: "center", marginTop: "50px" }}>
                {migrationCompleted && <Row className="mt-3 text-center">
                    <Col>
                        <div className="alert alert-success" style={{ position: "inherit"}} >
                            <FormattedMessage {...messages.migration_completed_success} />{" "}
                            <Icon value={"check"}/>
                        </div>
                    </Col>
                </Row>}
                {!migrationCompleted && <>
                    <div className="h2">
                        <FormattedMessage
                            id="nylas_migration_component.title"
                            defaultMessage="Action Required"
                        />
                        <Icon value={"exclamation"} style={{ color: "orange", marginLeft: "0.5rem" }} />
                    </div>
                    <div className="container mt-4 mb-4 text-left" style={{ width: "860px"}}>
                        <Row className="pl-3">
                            {isSchedulingEnabled 
                                ? <FormattedHTMLMessage {...messages.scheduling_users_text} /> 
                                : <FormattedHTMLMessage {...messages.email_users_text} />
                            }
                        </Row>
                        <Row className="mt-2 pl-3">
                            <FormattedHTMLMessage
                                id="nylas_migration_component.description_read_carefully"
                                defaultMessage={
                                    "<b class='text-danger'>Please read carefully " +
                                "before migrating your account:</b>"
                                }
                            />
                        </Row>
                        <Row className="mt-2 pl-3">
                            {!isSchedulingEnabled 
                                ? <>
                                    <ul>
                                        <li><FormattedHTMLMessage {...messages.email_users_bullet_1} /></li>
                                        <li><FormattedHTMLMessage {...messages.email_users_bullet_2} /></li>
                                        <li><FormattedHTMLMessage {...messages.email_users_bullet_3} /></li>
                                    </ul>
                                </>
                                : <>
                                    <ul>
                                        <li><FormattedHTMLMessage {...messages.scheduling_users_bullet_1} /></li>
                                        <li><FormattedHTMLMessage {...messages.scheduling_users_bullet_2} /></li>
                                        <li><FormattedHTMLMessage {...messages.scheduling_users_bullet_3} /></li>
                                    </ul>
                                </>}
                        </Row>
                        {isSchedulingEnabled && <div className="mt-2 alert alert-warning text-dark">
                            <Row className="px-2">
                                <FormattedHTMLMessage
                                    id="nylas_migration_component.description_header_p3"
                                    defaultMessage={"<b>For appointment users:</b>"}
                                />
                            </Row>
                            <Row className="mt-1 px-2">
                                <FormattedMessage
                                    id="nylas_migration_component.description_p4"
                                    defaultMessage={
                                        "After reconnecting your email, follow the steps in the support articles that " +
                                        "will be listed after the account migration is started to recreate " + 
                                        "your calendars and scheduling pages for customers to continue booking appointments."
                                    }
                                />
                            </Row>
                            <Row className="mt-1 px-2">
                                <FormattedHTMLMessage
                                    id="nylas_migration_component.description_p5"
                                    defaultMessage={
                                        "<b>Important: Do not recreate calendars or scheduling pages before reconnecting your email.</b> " +
                                        "<br/>Be prepared to set them up after migrating."
                                    }
                                />
                            </Row>
                        </div>}

                        <Row className="mt-3 pl-3">
                            {!isSchedulingEnabled 
                                ? <FormattedHTMLMessage {...messages.email_users_when_ready} /> 
                                : <FormattedHTMLMessage {...messages.scheduling_users_when_ready} />
                            }
                        </Row>
                    
                        {matchedUsers?.length > 0 && <Row className="text-left">
                            {renderNameColumn(matchedUsers)}
                            {renderEmailColumn(matchedUsers)}
                            {renderStatusColumn(matchedUsers)}
                        </Row>}

                    </div>
                    <div className="container pb-2" style={{ width: "860px"}}>
                        {!migrationFlagChanged && callToActionBtn}
                        {migrationFlagChanged && 
                        <>
                            <Row className="pb-4">
                                <Col>
                                    <div className="alert alert-success text-left" >
                                        {!isSchedulingEnabled ? <FormattedMessage {...messages.email_account_migration_success} /> 
                                            : <FormattedHTMLMessage {...messages.scheduling_account_migration_success} />
                                        }
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <IntegrationButtonWrapper
                                        module={AccountFeatures.FeatureCustomEmail}
                                        redirectToAccount={true}
                                        to={nylasUrl}
                                        integrationKey={getNylasVersion()}
                                        className={"btn btn-outline-primary"}
                                        protectedClassName={"btn btn-outline-primary"}
                                        premium={false}
                                        protectionRule={AppContext.accountFeatureIsEnabled(AccountFeatures.FeatureCustomEmail)}
                                    >
                                        <span>
                                            <Icon value={"envelope"} className={"mr-2 fa-lg"} />
                                            <FormattedMessage {...messages.connect_btn_label} />
                                        </span>
                                    </IntegrationButtonWrapper>
                                </Col>
                            </Row>
                        </>
                        }
                    </div>
                </>}
                {nylasKey === INTEGRATION_KEY.NYLASV3 && isSchedulingEnabled && schedulePagesListing.length > 0 && 
                    <div className="container mt-2 mb-4 text-left" style={{ width: "860px"}}>
                        <Row className="pt-4 pb-1">
                            <Col>
                                {migrationCompleted 
                                    ? <FormattedHTMLMessage {...messages.scheduling_users_reminder_migration_complete} />
                                    : <FormattedHTMLMessage {...messages.scheduling_users_reminder_migration_incomplete} />
                                }
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <ul>
                                    <li>
                                        <a 
                                            href="https://support.quotemachine.com/en/articles/5221801-setting-up-a-calendar-in-quotemachine" 
                                            target="_blank" 
                                            rel="noreferrer"
                                        >
                                            <FormattedHTMLMessage {...messages.scheduling_users_articles_2} />
                                        </a>
                                    </li>
                                    <li>
                                        <a 
                                            href="https://support.quotemachine.com/en/articles/5187798-creating-scheduling-pages-in-quotemachine"
                                            target="_blank" 
                                            rel="noreferrer"
                                        >
                                            <FormattedHTMLMessage {...messages.scheduling_users_articles_3} />
                                        </a>
                                    </li>
                                </ul>
                            </Col>
                        </Row>
                        <Row className="pl-3 pt-3 text-left">
                            <div className="h5">
                                <FormattedMessage {...messages.scheduling_page_list_table_header} />
                            </div>
                        </Row>
                        <Row className="pb-2 text-left">
                            {renderSchedulePageList(schedulePagesListing)}
                        </Row>
                    </div>
                }
            </div>
        </>
    )
}

export default injectIntl(NylasMigrationComponent)



// WEBPACK FOOTER //
// ./src/nylas/NylasMigrationComponent.tsx